/**
 * MonetizationOverview
 *
 * Bullet-list overview of premium-video / boost / unlock-earnings costs and
 * the "Insufficient coins → Recharge Wallet" link. Mirrors Flutter's
 * `_monetizationInfoWidget` from content_monetization_screen.dart.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { Bullet } from '@/presentation/components/common/Bullet';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { MathUtils } from '@/domain/utils/mathUtils';

interface MonetizationOverviewProps {
  allowPremium: boolean;
  premiumPriceCoins: number;
  isBoosted: boolean;
  boostPriceCoins: number;
  allowUnlock: boolean;
  unlockPriceCoins: number;
  shouldRecharge: boolean;
  rechargeAmount: number;
  onRechargeClick: () => void;
}

export const MonetizationOverview: React.FC<MonetizationOverviewProps> = ({
  allowPremium,
  premiumPriceCoins,
  isBoosted,
  boostPriceCoins,
  allowUnlock,
  unlockPriceCoins,
  shouldRecharge,
  rechargeAmount,
  onRechargeClick,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const showPremiumPrice = allowPremium && premiumPriceCoins > 0;
  const showBoostPrice = isBoosted && boostPriceCoins > 0;
  const showEarnings = allowUnlock && unlockPriceCoins > 0;
  const showProfitPoint = (showPremiumPrice || showBoostPrice) && showEarnings;
  const visible = showPremiumPrice || showBoostPrice || showEarnings || shouldRecharge;

  if (!visible) return null;

  const totalCost = premiumPriceCoins + boostPriceCoins;
  const unlocksNecessary = unlockPriceCoins > 0 ? Math.ceil(totalCost / unlockPriceCoins) : 0;

  const premiumRow = l10n.publish.premiumVideoRow.replace('{coins}', MathUtils.removeTrailingZero(premiumPriceCoins));
  const boostRow = l10n.publish.videoBoostRow.replace('{coins}', MathUtils.removeTrailingZero(boostPriceCoins));
  const earningsRow = l10n.publish.earningsRow.replace('{coins}', MathUtils.removeTrailingZero(unlockPriceCoins));
  const profitTemplate = unlocksNecessary > 1 ? l10n.publish.profitPointRowPlural : l10n.publish.profitPointRow;
  const profitText = profitTemplate.replace('{n}', String(unlocksNecessary));
  const insufficientText = l10n.publish.insufficientCoinsMonetization.replace('{amount}', String(rechargeAmount));

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
        <AssetIcon
          path="/assets/images/info.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.disabled}
        />
        <span
          style={{
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {showEarnings ? l10n.publish.monetizationOverview : l10n.publish.costsOverview}
        </span>
      </div>

      <div
        style={{
          marginTop: responsive.spacing.sm,
          backgroundColor: theme.secondary,
          borderRadius: responsive.borderRadius.md,
          padding: responsive.spacing.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: responsive.spacing.sm,
        }}
      >
        {showPremiumPrice && (
          <Bullet color={theme.text.primary} fontSize={responsive.font.sm}>
            <span {...qaAttr({ id: 'monetization-overview-premium', value: MathUtils.removeTrailingZero(premiumPriceCoins) })}>
              {premiumRow}
            </span>
          </Bullet>
        )}
        {showBoostPrice && (
          <Bullet color={theme.text.primary} fontSize={responsive.font.sm}>
            <span {...qaAttr({ id: 'monetization-overview-boost', value: MathUtils.removeTrailingZero(boostPriceCoins) })}>
              {boostRow}
            </span>
          </Bullet>
        )}
        {showEarnings && (
          <>
            <Bullet color={theme.text.primary} fontSize={responsive.font.sm}>
              <span {...qaAttr({ id: 'monetization-overview-earnings', value: MathUtils.removeTrailingZero(unlockPriceCoins) })}>
                {earningsRow}
              </span>
            </Bullet>
            <div
              style={{
                height: showProfitPoint || shouldRecharge ? responsive.spacing['2xl'] : responsive.spacing.sm,
              }}
            />
          </>
        )}
        {showProfitPoint && (
          <div
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.sm,
              fontWeight: responsive.fontWeight.semibold,
            }}
          >
            {profitText}
          </div>
        )}
        {shouldRecharge && (
          <div
            style={{
              marginTop: showProfitPoint ? responsive.spacing.sm : 0,
              fontSize: responsive.font.sm,
              color: theme.text.primary,
            }}
          >
            <span>{insufficientText}</span>
            <button
              {...qaAttr({ id: 'monetization-recharge-wallet', value: rechargeAmount })}
              onClick={onRechargeClick}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.blue,
                fontSize: responsive.font.sm,
                fontWeight: responsive.fontWeight.semibold,
                padding: 0,
              }}
            >
              {l10n.content.rechargeWallet}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
